import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { APP_CONFIG } from '../../app.config';
import { AppConfigInterface, LocationConfigInterface } from '../../config/app.config.interface';
import { WindowService } from './window.service';
import { TranslateService } from '@ngx-translate/core';
import find from 'lodash/find';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private locationId: SupportedLocationIdType;
  private locationFromDomain: SupportedLocationIdType | null = null;
  private locationsConfig: LocationConfigInterface[];

  constructor(
    @Inject(APP_CONFIG) private config: AppConfigInterface,
    @Inject(PLATFORM_ID) private platformId: string,
    private windowService: WindowService,
    private translate: TranslateService,
  ) {
    this.locationsConfig = this.config.locations;

    if (isPlatformServer(this.platformId)) {
      this.locationId = this.config.defaultLocationId;

      return;
    }

    this.locationFromDomain = this.getLocationFromDomain(this.getHost());
    this.locationId = this.locationFromDomain ? this.locationFromDomain : this.config.defaultLocationId;
  }

  private getHost(): string | undefined {
    if (isPlatformBrowser(this.platformId)) {
      return this.windowService?.nativeWindow?.location?.hostname;
    }

    return '';
  }

  private getLocationFromDomain(domain: string | undefined): SupportedLocationIdType | null {
    if(domain) {
      const locationFromConfig = this.locationsConfig.find((location: LocationConfigInterface) => location?.hostMatchRegex?.test(domain));

      return locationFromConfig ? locationFromConfig.id : null;
    }

    return null;
  }

  public init(): void {
    this.translate.use(this.locationId);
  }

  public getLocationId(): SupportedLocationIdType {
    return this.locationId;
  }

  public getApiLanguageCode(): string {
    const currentLocation = this.locationsConfig.find((location: LocationConfigInterface) => location.id === this.locationId);

    return currentLocation?.apiLanguageCode ?? this.config?.defaultLocationId;
  }

  public getAllLocations(): LocationConfigInterface[] {
    return this.locationsConfig;
  }

  public changeLocationForRootModule(locationId: SupportedLocationIdType): void {
    if (this.locationId === locationId) {
      return;
    }
    if (this.isLocationLoadedFromDomain()) {
      const urlToRedirect = find(this.locationsConfig, { id: locationId })?.dedicatedUrl;
      if(urlToRedirect) {
        this.windowService.nativeWindow.location.href = urlToRedirect;
      }

      return;
    }
    this.changeDynamicLocation(locationId);
  }

  public getLocationUrl(): string | undefined {
    const locationId = this.getLocationId();
    if (locationId) {
      return find(this.locationsConfig, { id: locationId })?.dedicatedUrl;
    }

    return find(this.locationsConfig, { id: this.config.defaultLocationId })?.dedicatedUrl;
  }

  public changeLocationForChildrenModule(locationId: SupportedLocationIdType): void {
    if (this.isLocationLoadedFromDomain()) {
      this.translate.use(this.locationId);
      return;
    }
    this.changeDynamicLocation(locationId);
  }

  private isLocationLoadedFromDomain(): boolean {
    return !!this.locationFromDomain;
  }

  private changeDynamicLocation(locationId: SupportedLocationIdType): void {
    this.locationId = locationId;
    this.translate.use(this.locationId);
  }

  public getDatePickerFormat(): string | undefined {
    return this.locationsConfig.find((location: LocationConfigInterface) => location.id === this.locationId)?.datePickerDateFormat;
  }

  public getDefaultCountryCode(): string | undefined {
    return this.locationsConfig.find((location: LocationConfigInterface) => location.id === this.locationId)?.defaultCountryCode;
  }

  public getDefaultLocation(): LocationConfigInterface | undefined {
    return this.locationsConfig.find((location: LocationConfigInterface) => location.id === this.locationId);
  }
}
